/**
 * Плагин для обработки ошибки загрузки динамических модулей.
 * Эта ошибка возникает в Firefox при обновлении страницы до завершения загрузки JS-чанков.
 * Браузер прерывает запросы (NS_BINDING_ABORTED), и Nuxt интерпретирует это как ошибку.
 */

const STORAGE_KEY = 'chunk_error_reload';
const MAX_RELOADS = 5;
const RELOAD_WINDOW_MS = 10000; // 10 секунд

interface ReloadData {
  count: number;
  timestamp: number;
}

function getReloadData(): ReloadData {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
  }
  catch {
    // sessionStorage недоступен или данные повреждены
  }
  return { count: 0, timestamp: 0 };
}

function setReloadData(data: ReloadData): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
  catch {
    // sessionStorage недоступен
  }
}

function clearReloadData(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  catch {
    // sessionStorage недоступен
  }
}

function canReload(): boolean {
  const data = getReloadData();
  const now = Date.now();

  // Если прошло больше 10 секунд — сбрасываем счётчик
  if (now - data.timestamp > RELOAD_WINDOW_MS) {
    setReloadData({ count: 1, timestamp: now });
    return true;
  }

  // Если перезагрузок меньше лимита — разрешаем
  if (data.count < MAX_RELOADS) {
    setReloadData({ count: data.count + 1, timestamp: data.timestamp });
    return true;
  }

  // Лимит исчерпан — показываем ошибку пользователю
  return false;
}

function isChunkLoadError(message: string): boolean {
  return message.includes('error loading dynamically imported module')
    || message.includes('Failed to fetch dynamically imported module')
    || message.includes('Unable to preload CSS')
    || message.includes('Loading chunk')
    || message.includes('ChunkLoadError');
}

export default defineNuxtPlugin((nuxtApp) => {
  // При успешной загрузке очищаем счётчик перезагрузок
  nuxtApp.hook('app:mounted', () => {
    clearReloadData();
  });

  nuxtApp.hook('app:error', (error) => {
    if (isChunkLoadError(error?.message || '')) {
      if (canReload()) {
        clearError();
        window.location.reload();
      }
      // Если лимит исчерпан — ошибка покажется пользователю как обычно
    }
  });

  nuxtApp.hook('vue:error', (error) => {
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (isChunkLoadError(errorMessage)) {
      if (canReload()) {
        window.location.reload();
      }
    }
  });
});
